import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import usePageTitle from '../../hooks/UsePageTitle';

const FinancialDueDiligence = () => {
  usePageTitle('Financial Due Diligence');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Financial Due Diligence</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/Financial Due Diligence.jpg' 
            alt="Financial Due Diligence" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              In any transaction — whether it's an acquisition, investment, merger, or joint venture — 
              the financial health of the target entity is central to your decision-making. But audited 
              statements alone don't tell the full story. That's where Financial Due Diligence (FDD) comes in.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we specialize in uncovering the financial truths behind the numbers — 
              ensuring you invest, acquire, or partner with confidence.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              Our Financial Due Diligence goes far beyond a surface-level review. We dive deep into the 
              financial operations, past performance, cash flows, debt, working capital, revenue quality, 
              and potential liabilities to provide a clear picture of the target entity's true financial standing.
            </p>
          </Card>
        </div>

        {/* Why FDD is Critical Section */}
        <div className="col-12 mt-5">
          <h2>Why Financial Due Diligence is Critical</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Even a profitable-looking business may hide issues like aggressive revenue recognition, 
                off-the-book liabilities, tax exposures, or cash flow mismatches. FDD helps detect:
              </p>
              <ul className="text-xl line-height-3 ml-4">
                <li className="mb-2">Overstated profits or revenues</li>
                <li className="mb-2">Unsustainable margins</li>
                <li className="mb-2">Related-party leakages</li>
                <li className="mb-2">Tax liabilities</li>
                <li>Inadequate provisioning or contingent risks</li>
              </ul>
              <p className="text-xl line-height-3 mt-3 font-semibold text-primary">
                It gives you the power to negotiate better, price fairly, and structure deals smarter.
              </p>
            </div>
            <img 
              src='/images/financing2.png' 
              alt="Financial Analysis" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
          </div>
        </div>

        {/* Process Section */}
        <div className="col-12 mt-5">
          <h2>Our Financial Due Diligence Process</h2>
          <p className="text-xl mb-4">
            SnP Finvisors follows a structured, insight-driven process for FDD that blends forensic scrutiny with commercial acumen.
          </p>
          <Divider />
          
          <div className="grid">
            {/* Process Item 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Historical Financial Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of past 3–5 years of audited financial statements and management accounts.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Examination of trends in revenue, gross margins, EBITDA, and net profit.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of anomalies, restatements, and extraordinary items.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Quality of Earnings (QoE) Analysis" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Breakdown of recurring vs. non-recurring income and expenses.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Adjusted EBITDA calculations for normalized profitability.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>One-time gains/losses, inter-company transactions, and unusual accounting practices.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Working Capital Assessment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of receivables, payables, inventory, and their turnover cycles.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of seasonal fluctuations, credit risks, and provisioning adequacy.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of normalized working capital requirements for operations.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Cash Flow and Debt Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Analysis of operating vs. investing vs. financing cash flows.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Debt schedule review: type of borrowings, repayment schedules, covenants, and security.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of undisclosed or contingent liabilities and financial guarantees.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Revenue Recognition & Cost Validation" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Scrutiny of revenue recognition policies and their alignment with Ind AS/IFRS.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Verification of customer contracts and invoicing logic.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Margin review by product, service line, customer, or geography.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Tax & Statutory Compliance Review" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of historical tax filings (Income Tax, GST, TDS).</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of open tax litigations, notices, and likely outcomes.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assessment of tax provisioning adequacy and deferred tax positions.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Related Party Transactions" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification and examination of transactions with group companies, directors, or key personnel.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of inter-company loans, asset transfers, royalty/commission arrangements.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation of transfer pricing policies and regulatory disclosures.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Forecast & Budgeting Assessment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Review of internal budgets and forecasts shared with investors or lenders.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Stress testing of assumptions used for revenue growth, cost reduction, and EBITDA projections.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Validation of CapEx plans, funding needs, and cash runway.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Process Item 9 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="9. Red Flag Reporting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Summary of key financial risks and their commercial impact.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Issues that may require deal restructuring, indemnities, or price adjustments.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Mitigation strategies and post-transaction action plans.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Applications Section */}
        <div className="col-12 mt-5">
          <Card>
            <h2 className="mt-0">Key Applications of Financial Due Diligence</h2>
            <Divider />
            <div className="grid">
              <div className="col-12 md:col-6 lg:col-3">
                <div className="flex align-items-center mb-3">
                  <i className="pi pi-briefcase text-primary mr-3 text-xl"></i>
                  <span className="font-semibold">M&A transactions (both buy-side and sell-side)</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-3">
                <div className="flex align-items-center mb-3">
                  <i className="pi pi-chart-line text-primary mr-3 text-xl"></i>
                  <span className="font-semibold">PE/VC funding or strategic investments</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-3">
                <div className="flex align-items-center mb-3">
                  <i className="pi pi-users text-primary mr-3 text-xl"></i>
                  <span className="font-semibold">Promoter buyouts or spin-offs</span>
                </div>
              </div>
              <div className="col-12 md:col-6 lg:col-3">
                <div className="flex align-items-center mb-3">
                  <i className="pi pi-money-bill text-primary mr-3 text-xl"></i>
                  <span className="font-semibold">Business sales and valuation assessment</span>
                </div>
              </div>
              <div className="col-12">
                <div className="flex align-items-center">
                  <i className="pi pi-shield text-primary mr-3 text-xl"></i>
                  <span className="font-semibold">Lenders evaluating credit risk for high-value clients</span>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <div className="flex-1">
              <h2>Financial Clarity for Confident Transactions</h2>
              <Divider />
              <p className="text-xl line-height-3">
                At SnP Finvisors, we transform complex financial data into actionable insights. Our Financial Due Diligence 
                services provide the clarity you need to make informed decisions, negotiate from a position of strength, 
                and structure deals that protect your interests while maximizing value.
              </p>
            </div>
            <img 
              src='/images/financing3.png' 
              alt="Financial Due Diligence Solutions" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialDueDiligence;